// /ae - UAE country home. Rendered by components/market/MarketHomePage with
// the Singapore homepage's sections, so the shape follows those sections.
// Copy is written for the UAE: "AC" only (never "aircon"), Dubai and Abu
// Dhabi described separately, nothing lifted from /sg.
const content = {
    title: (c) => `AC Service & Personal Trainers in Dubai and Abu Dhabi | StringsSG UAE`,
    description: (c) =>
        `AC cleaning from ${c.money(c.tables.general[0].wall)} and personal trainers from ${c.money(c.lowestPt, 0)} a session in Dubai and Abu Dhabi. Upfront prices, booked online.`,
    heroAlt: 'StringsSG home services for Dubai and Abu Dhabi residents',

    hero: {
        title: 'AC Service & Personal Trainers',
        highlight: 'in Dubai & Abu Dhabi',
        sub: (c) =>
            `Fixed, upfront prices for AC cleaning, coil washing, gas refills and repairs, and certified personal trainers who come to your home or building gym. AC cleaning from ${c.money(c.tables.general[0].wall)}.`,
        badges: ['Upfront Prices', 'Female Trainers on Request', 'Building-Ready Paperwork'],
    },

    services: {
        title: 'Two Services for UAE Homes, Done Properly',
        intro: 'Cooling and fitness are what UAE residents ask us for most, so that is where we started.',
        aircon: {
            title: 'AC Service UAE',
            description: 'Cleaning, coil chemical wash, deep cleaning, gas refill, repairs and yearly maintenance contracts for split, ducted and cassette units.',
            ctaText: 'See AC service prices in Dubai',
            imageAlt: 'AC technician arriving for a service in a UAE home',
        },
        pt: {
            title: 'Personal Trainers UAE',
            description: 'One-to-one training at home, in your building gym or outdoors in the cooler months, with female trainers on request.',
            ctaText: 'See personal trainer prices in Dubai',
            imageAlt: 'Female personal trainer coaching a client',
        },
    },

    // One column per city and service (components/index/ModernHomePage
    // LocalServicesSection). Links come from data/markets/registry.js.
    local: {
        dubai: {
            aircon: {
                heading: 'AC Service in Dubai',
                body: (c) =>
                    `From Marina and JLT towers to villas in Arabian Ranches and Mirdif, we clean and repair split, ducted and district-cooled systems. AC cleaning starts at ${c.money(c.tables.general[0].wall)} for one unit, and every service and unit count is priced on the page before you book.`,
            },
            pt: {
                heading: 'Personal Trainer in Dubai',
                body: (c) =>
                    `Certified trainers for weight loss, strength, pre- and postnatal fitness and seniors, from ${c.money(c.lowestPt, 0)} a session. Train in your tower gym, at home or on the beach once the weather cools.`,
            },
        },
        'abu-dhabi': {
            aircon: {
                heading: 'AC Service in Abu Dhabi',
                body: 'Island apartments on Reem, Saadiyat and Yas, and mainland villas from Khalifa City to Mohammed Bin Zayed City. Coastal humidity means coils foul faster here, so we set the service rhythm by building, not by habit.',
            },
            pt: {
                heading: 'Personal Trainer in Abu Dhabi',
                body: 'Coaching that fits Abu Dhabi routines: early sessions before the commute, compound and community gyms, and women trainers for ladies-only sessions at home.',
            },
        },
    },

    trust: {
        title: 'Built for UAE Homes',
        intro: 'Ten months of cooling, desert dust and sea humidity ask more of an AC unit than most climates. Our services and plans are set up for that.',
        items: [
            { title: 'Prices Before You Book', body: 'Every AC price and training package is on the page. No call-out fees, no "price on inspection" for routine work' },
            { title: 'Dust and Humidity, Both Covered', body: 'Inland dust and coastal humidity need different service rhythms, and our pages explain both' },
            { title: 'Training That Fits the Climate', body: 'Indoor plans for summer, outdoor sessions in winter, and schedules that move around Ramadan' },
            { title: 'Building-Ready', body: 'Technician and trainer details shared in advance for building permits and gate passes' },
        ],
        card: {
            title: 'Lower DEWA and TAQA Bills',
            body: 'A clean coil cools with less effort. Regular servicing keeps the unit cooling and the bill in check through the long summer.',
        },
    },

    stepsTitle: 'Booking in Four Steps',
    steps: [
        { title: 'Choose the service', body: 'Pick an AC service and your units, or a training package - prices are shown upfront.' },
        { title: 'Send your request', body: 'Tell us your area and a convenient time.' },
        {
            title: (c) => (c.market.paymentsEnabled ? 'Pay securely online' : 'Confirm in the app'),
            body: (c) => (c.market.paymentsEnabled
                ? 'Pay by card, Google Pay or Apple Pay in your StringsSG account - now, or after the service.'
                : 'Confirm your request in your StringsSG account - no card needed. We assign your technician or trainer.'),
        },
        { title: 'Service at your home', body: 'Nothing beyond what you booked is done without your agreement.' },
    ],

    ctaTitle: 'Ready for a Cooler, Fitter Home?',
    ctaBody: 'Create your StringsSG account and book AC service or a personal trainer in Dubai or Abu Dhabi.',

    technology: {
        intro: 'The StringsSG platform, set up for the UAE: prices in dirhams, bookings in your time zone and our team a message away.',
        features: [
            { title: 'RedDotAI Assistant', description: 'Ask about AC problems or training plans and get answers built on our UAE service pages.' },
            { title: 'Prices in AED', description: 'AC and training prices are calculated in dirhams from your units or package, before you send a request.' },
            { title: 'Your Requests in One Place', description: 'Send, track and reschedule AC visits and training sessions from your StringsSG account.' },
            {
                title: (c) => (c.market.paymentsEnabled ? 'Secure Payments' : 'No Card Needed to Book'),
                description: (c) => (c.market.paymentsEnabled
                    ? 'Pay by card, Apple Pay or Google Pay, encrypted with TLS 1.3 - now or after the service.'
                    : 'Confirm a request without a card. Our team arranges payment with you before the visit.'),
            },
            { title: 'Checked Professionals', description: 'Technicians and trainers are identity- and background-checked before their first UAE job.' },
            { title: 'Reminders and Updates', description: 'Service reminders before the summer peak and live updates on the day of your visit.' },
        ],
        appPoints: [
            'Requests and confirmations for Dubai and Abu Dhabi',
            'Chat with our UAE team',
            'Service reports after every AC visit',
            'Reminders for your next service',
        ],
    },

    partners: {
        title: 'Technicians and Trainers: Grow With StringsSG in the UAE',
        intro: 'We are building our teams in Dubai and Abu Dhabi. You do the work; we bring the customers and run the scheduling and invoicing.',
        items: [
            { title: 'Customers Without Ad Spend', body: 'Requests from Dubai and Abu Dhabi homes come to you' },
            { title: 'Admin Handled', body: 'Scheduling, reminders and invoices run on our platform' },
            { title: 'Reliable Payouts', body: 'Get paid for every completed job' },
            { title: 'A Profile That Grows', body: 'Ratings and reviews build your reputation with each job' },
        ],
    },

    guidesTitle: 'Guides for UAE Residents',
    faqs: [
        {
            question: 'Which areas of the UAE do you cover?',
            answer: 'Dubai and Abu Dhabi, including the main residential communities in each city.',
        },
        {
            question: 'How do I pay?',
            answer: (c) => (c.market.paymentsEnabled
                ? 'Online in your StringsSG account, in AED: by card, Google Pay or Apple Pay. Choose Pay Now, or Pay Later to pay once the service is done.'
                : 'Confirm your request in the StringsSG app - no card is needed when you book. Our team arranges payment with you before the visit.'),
        },
        {
            question: 'Who is StringsSG?',
            answer: 'StringsSG has run home services since 2018 - AC servicing and personal training first among them - and now serves Dubai and Abu Dhabi.',
        },
    ],
}

export default content
